import * as Sentry from '@sentry/react';
import type { UnitInfoDataInterface } from 'store/unitInfoData/types';

const SCHEMA_VERSION = 3;
const CACHE_KEY = `sandvik_unit_info_data_v${SCHEMA_VERSION}`;

interface CachedData {
  data: UnitInfoDataInterface;
  timestamp: number;
}

class UnitInfoDataCache {
  private isValidCacheData(data: unknown): data is UnitInfoDataInterface {
    if (!data || typeof data !== 'object') return false;
    // We can add specific validations on cached data here
    return true;
  }
  async get(): Promise<UnitInfoDataInterface | null> {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const parsedCache: CachedData = JSON.parse(cached);

      if (!this.isValidCacheData(parsedCache.data)) {
        throw new Error('Incompatible cache data structure');
      }

      return parsedCache.data;
    } catch (error) {
      // NOTE: Expected when cache is corrupted or schema changed
      Sentry.captureException(error, {
        tags: { cache: 'unitInfoDataCache' },
        level: 'warning',
      });
      void this.clear();
      return null;
    }
  }

  async set(data: UnitInfoDataInterface): Promise<void> {
    try {
      const cachedData: CachedData = {
        data,
        timestamp: Date.now(),
      };

      localStorage.setItem(CACHE_KEY, JSON.stringify(cachedData));
    } catch (error) {
      // NOTE: Expected when localStorage quota exceeded or disabled
      Sentry.captureException(error, {
        tags: { cache: 'unitInfoDataCache' },
        level: 'warning',
      });
      console.warn('Failed to cache unitInfoData:', error);
    }
  }

  async clear(): Promise<void> {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { cache: 'unitInfoDataCache', operation: 'clear' },
        level: 'warning',
      });
    }
  }
}

export const unitInfoDataCache = new UnitInfoDataCache();
